<template lang="pug">
  liondesk-dialog(
    v-model='showModal',
    ok-button-text='Add',
    :dialog-title='"Add Contact Relationship"',
    :width='"600px"',
    :disable-ok-button='shouldDisableBtn',
    @click:ok='addRelationshipTypes',
    @click:cancel='onCloseModal'
  )
    div(slot='body').dialog-main-container
      v-layout(column, align-center, justify-start)
        v-autocomplete(
          :search-input.sync="search"
          :items='searchedContacts'
          :menu-props="{ closeOnClick: false, closeOnContentClick: false }"
          :hide-no-data="!(showSearchMenu && isSearchedContactsEmpty && search.length)"
          append-icon=''
          chips
          flat
          multiple
          no-filter
          hide-details
          clearable
        ).contacts-list
          template(v-slot:selection="")
          template(slot='label').pa-0
            v-layout(row, align-center, justify-start)
              v-icon.search-field-icon.font-weight-bold search
              span.pa-0.ml-2.search-field-label Search and select an existing Contact
          template(v-if='showSearchMenu && !isSearchedContactsEmpty' slot='item' slot-scope='{ item }')
            v-list-tile-content
              v-list-tile-action.pa-0.ma-0
                v-checkbox(
                  v-if="item.id"
                  v-model='item.isSelected',
                  :disabled='checkboxDisabled || item.hasRelationship',
                  :indeterminate='item.hasRelationship'
                  color='#235ea1',
                  hide-details
                  @change='updateSelected(item)',
                ).contacts-checkbox
            v-list-tile-title(
              v-if="item.id"
              :class='item.hasRelationship ? "contacts-names-disabled" : "contacts-names primary--text"'
            ) {{ item.first_name }} {{ item.last_name }}
              span(v-if='item.hasRelationship').ml-1.contacts-names-disabled
                | (already related)
          template(slot='no-data')
            v-list-tile.contact-tile
              v-list-tile-content.pa-0
                v-list-tile-title
                  span.no-contacts-found.font-italic  No matching contacts.
        v-btn(v-if='!selectedContacts.length' @click="showCreateContact").primary.mt-4.mb-2
          v-icon person_add
          span.ml-2 Create a New Contact
        v-list.selected-contacts.mt-4.pa-0(v-else)
          v-list-tile(v-for='selected in selectedContacts', :key='selected.id').mb-2.pa-2
            v-layout(column).pa-0
              v-list-tile-content.pa-0.selected-contact-content.mb-1
                v-list-tile-title.selected-contact-name.font-weight-bold {{ selected.first_name }} {{ selected.last_name }}
              v-list-tile-content.selected-contact-content
                v-menu(offset-y)
                  v-toolbar-title(
                    slot='activator',
                    :class='selected.relationshipType ? "relationship-menu-toolbar--selected" : ""'
                  ).relationship-menu-toolbar.px-1
                    span(
                      :class='selected.relationshipType ? "relationship-menu-title--selected" : ""'
                    ).relationship-menu-title {{ selected.relationshipType ? selected.relationshipType.textCustomerType : relationshipMenuTitle }}
                    v-icon(
                      :class='selected.relationshipType ? "relationship-menu-icon--selected" : ""'
                    ).relationship-menu-icon arrow_drop_down
                  v-list
                    v-list-tile(
                      v-for='type in selected.relationshipTypes',
                      :key='type.idCustomerType',
                      :disabled='type.disabled'
                      @click='selectedType(type, selected)'
                    )
                      v-list-tile-title
                        | {{ type.textCustomerType }}
              v-list-tile-content(v-if="selected.hasError").selected-contact-content
                v-list-tile-sub-title
                  span.errorMsg {{ selected.errorMsg }}
            v-list-tile-action
              v-icon(@click='removeSingleSelectedContact(selected)').selected-contact-removeIcon delete
        v-layout(
          v-if='selectedContacts.length',
          column,
          align-start,
          justify-center
        ).caption-container.mt-1
          v-layout(row, align-center, justify-space-between).caption-text-container.mb-1
            p(v-if='showRequiredMsg').caption-text.caption-text--required *Required field
            v-spacer(v-if='!showRequiredMsg')
            p(
              v-if='shouldShowRemoveAllBtn',
              @click='removeAll()'
            ).caption-text.caption-text--remove
              | Remove all selected contacts
          p.caption-text.caption-text--limit.pa-0.ma-0 You can create more relationship types on your settings page
          p.caption-text.caption-text--limit.pa-0.ma-0 There is a maximum of 10 contacts to relate at a time.

</template>
<script>
import { mapState, mapActions, mapGetters, mapMutations } from 'vuex';
import LiondeskDialog from '@/Core/components/LiondeskDialog';
import Modal from '@/Core/mixins/modal';
import { debounce, prepareRequest } from '@/Core/utilities';
import { RELATION_TYPE_SPOUSE } from '@/Contacts/store/contact-relationships';

export default {
  name: 'ContactRelationshipModal',
  components: {
    LiondeskDialog,
  },
  mixins: [Modal],
  props: {
    expandDescription: {
      type: Boolean,
      default: false,
    },
    contactId: {
      type: Number,
      default: 0,
    }
  },
  data() {
    return {
      positionX: 0,
      positionY: 0,
      search: '',
      searchedContacts: [],
      selectedContacts: [],
      shouldDisableBtn: true,
      relationshipMenuTitle: 'Select type of relationship*',
      showRequiredMsg: true,
      disableSearchField: false,
      checkboxDisabled: true,
    };
  },

  watch: {
    search(searchTerm) {
      if(!searchTerm) this.searchedContacts = [];
      this.performSearch(searchTerm);
    },

    searchResults(val) {
      this.searchedContacts = JSON.parse(JSON.stringify(val));
      this.searchedContacts = this.searchedContacts.map(val => {
        val.hasRelationship = this.relatedContactsIds.includes(val.id);
        return val;
      });
      this.updateSelected();
    },

    selectedContacts(contacts) {
      let contactsLength = contacts.length;

      this.disableSearchField = contactsLength > 9;
      this.checkboxDisabled = contactsLength > 9;

      const findUnselectedRelationship = contacts.find(c => !c.relationshipType);

      this.showRequiredMsg = !!findUnselectedRelationship;
      this.shouldDisableBtn = (!!findUnselectedRelationship || !contactsLength);
    },

    openModal(val) {
      if(val) this.onOpenModal();
    },
  },

  async created() {
    await this.findCurrentUser();
  },

  computed: {
    ...mapState('contactRelationships', [
      'showSearchMenu',
      'searchResults',
      'relatedContacts',
      'relatedContactsIds',
      'relatedContactsError',
      'showModal',
      'relsErrors',
      'responseSaveRelationship',
    ]),
    ...mapState('contact', ['currentUser']),
    ...mapGetters('contactRelationships', ['relationshipTypes', 'relationshipsSpouse', 'relsErrorsObj']),

    shouldShowRemoveAllBtn() {
      return this.selectedContacts.length > 1;
    },

    isSearchedContactsEmpty() {
      return !this.searchedContacts.length;
    },

    isSpouseSelected() {
      const isSpouseAdded = this.relationshipsSpouse.length;
      const isSpouseAddedTemp = this.selectedContacts.filter( p => p.relationshipType && p.relationshipType.textCustomerType == RELATION_TYPE_SPOUSE).length;
      return isSpouseAdded || isSpouseAddedTemp;
    },

    relationshipTypesFilter() {
      if (this.isSpouseSelected) {
        return {
          ...this.relationshipTypes.map(r => {
            r.disabled = r.textCustomerType === RELATION_TYPE_SPOUSE;
            return r;
          })
        }
      }
      return this.relationshipTypes.map( relationship => { return {...relationship, disabled: false}} );
    },
  },

  methods: {
    ...mapMutations('contactRelationships',['resetRelationshipsErrorsResponse']),
    ...mapActions('contactRelationships', [
        'createContactRelationships',
        'fetchContactRelationships',
        'fetchRelationshipTypes',
        'searchContacts',
        'setShowModal',
        'setShowSearchMenu',
        'setUnselectSingleContact',
        'setUnselectAllContacts',
        'resetRelationshipsErrorsResponse',
      ]),
    ...mapActions('contact', ['findCurrentUser', 'setRelationshipActions']),

    async performSearch(searchTerm) {
      if (!this.currentUser || !this.currentUser.data) return
      const payload = {
        search: searchTerm,
        selectedContacts: this.selectedContacts,
        currentUserId: this.currentUser.data.me.id,
        contactId: this.contactId,
      };

      debounce(prepareRequest, [payload, this.searchContacts]);
    },

    async addRelationshipTypes() {
      const relationships = this.selectedContacts.map(val => {
        const selectedRelationship = {
          contact_id: val.id,
          contact_type_id: val.relationshipType.id,
        };
        return selectedRelationship;
      });

      await this.createContactRelationships({contactId: this.contactId, relationships});
      if(this.relsErrors) {
        if(this.responseSaveRelationship && this.responseSaveRelationship.length > 0) await this.fetchContactRelationships(this.contactId);
        this.filterAndAssignErrors();
        return;
      }

      this.onCloseModal();
      this.$emit('saveSnackbar');
    },

    //Removes successful relationships from selectedContacts data
    //and add 'hasError' flag to selectedContacts for which the response of createContactRelationships returned an error
    filterAndAssignErrors() {
      const newSelectedContacts = [];
      this.selectedContacts.forEach(contact => {
        if(this.relsErrorsObj.hasOwnProperty(contact.id)) {
          const errMsg = this.relsErrorsObj[contact.id].replace(/unauthorized \| contactID:\s\d+\s/, "");
          contact.hasError = true;
          contact.errorMsg = this.capitalizeFirstLetter(errMsg);
          newSelectedContacts.push(contact);
          this.selectedContacts = newSelectedContacts;
        }
      });
    },

    capitalizeFirstLetter(s) {
      return s[0].toUpperCase() + s.slice(1);
    },

    changeDescriptionView() {
      this.expandDescription = !this.expandDescription;
    },

    onCloseModal() {
      this.setShowModal(false);
      this.removeAll();
      this.search = '';
      this.setShowSearchMenu(false);
      this.selectedContacts = [];
      this.resetRelationshipsErrorsResponse();
    },

    updateSelected(contact) {
      const searchContacts = this.searchedContacts.filter(c => c.isSelected).map((searchItem) => {
        return {
          ...searchItem,
          relationshipTypes: this.relationshipTypesFilter
        }
      });
      this.selectedContacts = [
        ...this.selectedContacts.filter(c => c.isSelected),
        ...searchContacts
        ];

      let cache = {};
      let removeDuplicatedContacts = [];
      this.selectedContacts.map(val => {
        if(!cache[val.id]) {
          cache[val.id] = true;
          removeDuplicatedContacts = [...removeDuplicatedContacts, val];
        }
      });

      this.selectedContacts = [...removeDuplicatedContacts];
      if(contact && !contact.isSelected) {
        this.selectedContacts = [...this.selectedContacts.filter(c => c.id !== contact.id)];
      }
    },

    selectedType(type, contact) {
      this.selectedContacts = [
        ...this.selectedContacts.map(val => {
          if (val.id === contact.id) {
            val.relationshipType = {
              id: type.idCustomerType,
              textCustomerType: type.textCustomerType,
            };
          }

          return val;
        }),
      ];
      this.updateSelectItems(contact);
    },

    removeSingleSelectedContact(singleContact) {
      this.selectedContacts = [...this.selectedContacts.filter(c => (c.id !== singleContact.id))];
      const data = {
        searchedContacts: this.searchedContacts,
        contact: singleContact,
      };
      this.setUnselectSingleContact(data);
      this.updateSelectItems(singleContact);
    },

    removeAll() {
      this.setUnselectAllContacts(this.searchedContacts);
      this.selectedContacts = [];
    },

    showCreateContact() {
      this.setRelationshipActions(this.contactId);
      this.setShowModal(false);
    },

    updateSelectItems(contact) {
      this.selectedContacts = [
        ...this.selectedContacts.map(val => {
          if (val.relationshipType !== RELATION_TYPE_SPOUSE && val.id !== contact.id) {
            val.relationshipTypes = this.relationshipTypesFilter;
          }
          return val;
        }),
      ];
    }

  },
};
</script>

<style lang="scss" scoped>

.errorMsg {
  color: var(--v-error-lighten1);
}

.menuable__content__active {
  max-height: 50vh;
}

.search-field {
  width: 550px;
}

.search-field-label {
  color: var(--v-secondary-lighten5);
  font-size: 15px;
}

.search-field-icon {
  color: var(--v-secondary-lighten4);
  font-size: 20px;
  padding: 0;
}

.contacts-list {
  width: 500px;
  max-height: 200px;
  min-height: 60px;
  overflow: auto;
  overflow-x: hidden;
}

.v-menu__content {
  min-width: 450px !important;
  margin-left: 25px;
  -webkit-box-shadow: 0 0 1px rgb(0, 0, 0);
  box-shadow: 0 0 5px rgba(0, 0, 0, 0.4);
}

.v-list__tile__action {
  min-width: 24px;
  max-height: 30px;
}
.v-list__tile__content,
.v-list__tile__action {
  height: 30px;
}

.contacts-checkbox {
  transform: scale(0.8);
}

.contacts-names {
  font-size: 16px;
  color: var(-v-primary-base);
}

.contacts-names-disabled {
  font-size: 16px;
  color: var(--v-text-lighten5);
}

.no-contacts-found {
  font-size: 15px;
}

.v-label .v-input {
  font-size: 10px !important;
}

.contact-tile {
  height: 35px;
}

.selected-contacts {
  width: 550px;
  max-height: 220px;
  overflow: auto;
}

/deep/.v-label {
  width: 530px;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
}

.selected-contact-content {
  height: 22px;
}

.selected-contact-name {
  font-size: 16px;
}

.relationship-menu-toolbar {
  width: 500px;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  border-bottom: 5px solid var(--v-error-lighten4);
  &--selected {
    border-bottom: 5px solid var(--v-background-lighten3);
  }
}

.relationship-menu-title {
  font-size: 15px;
  color: var(--v-error-lighten1);
  &--selected {
    color: var(--v-secondary-lighten3);
  }
}

.relationship-menu-icon {
  transform: scale(0.7);
  color: var(--v-error-lighten1);
  &--selected {
    color: var(--v-secondary-lighten3);
  }
}

.v-input {
  font-size: 16px;
  color: var(--v-secondary-lighten5);
}

.selected-contact-removeIcon {
  transform: scale(0.6);
  color: var(--v-secondary-lighten3);
}

.caption-text-container {
  width: 520px;
}

.caption-text {
  font-size: 12px;
  &--required {
    color: var(--v-error-lighten1);
  }
  &--remove {
    font-weight: bold;
    color: var(--v-primary-base);
    cursor: pointer;
    &:hover {
      color: var(--v-primary-lighten2);
    }
  }
}

/deep/.v-chip {
  display: none;
}
</style>
